import { type FilteredMetadata, useRateManagement, useRatebooks } from '@api/useFreight/useFreight'
import { useQueryClient } from '@tanstack/react-query'
import { Form } from 'antd'
import { message } from 'antd'
import type { FormInstance } from 'antd/lib/form'
import { useEffect, useState } from 'react'
import type { Ratebook } from '../../../../../../../api/useFreight/types'
import type { FormData } from '../types'
import { initializeAddressInfo } from '../utils'

interface UseInvoiceConfigFormProps {
  ratebook: Ratebook
  invoiceStatus: string
  onChange?: (field: string, value: unknown) => void
}

interface UseInvoiceConfigFormReturn {
  form: FormInstance<FormData>
  isFormChanged: boolean
  isMetadataLoading: boolean
  metadata: FilteredMetadata | undefined
  handleFormChange: () => void
  handleSave: () => Promise<void>
  handleCancel: () => void
}

export function useInvoiceConfigForm({
  ratebook,
  invoiceStatus,
  onChange,
}: UseInvoiceConfigFormProps): UseInvoiceConfigFormReturn {
  const [form] = Form.useForm<FormData>()
  const queryClient = useQueryClient()
  const { useCreateRatebookMutation } = useRatebooks()
  const mutation = useCreateRatebookMutation()
  const { useFilteredMetadataQuery } = useRateManagement()
  const { data: metadata, isLoading: isMetadataLoading } = useFilteredMetadataQuery(ratebook)

  // Initialize form with ratebook data
  useEffect(() => {
    form.setFieldsValue({
      invoice_terms: {
        label: ratebook.invoice_configuration?.invoice_terms?.name,
        value: ratebook.invoice_configuration?.invoice_terms?.days,
      },
      send_emails: ratebook.invoice_configuration?.send_emails ?? true,
      billing_volume_basis: ratebook.invoice_configuration?.billing_volume_basis,
      billing_frequency: ratebook.invoice_configuration?.billing_frequency,
      invoice_grouping: ratebook.invoice_configuration?.invoice_grouping,
      freight_method: ratebook.freight_method || '',
      freight_date_method: ratebook.freight_date_method || 'delivery_date',
      invoice_status: invoiceStatus,
      recipients: ratebook.invoice_configuration?.recipients || [],
      product_minimums: Array.isArray(ratebook.product_minimums) ? [...ratebook.product_minimums] : [],
      address_info: initializeAddressInfo(ratebook.address_info),
      invoice_day_of_week: ratebook.invoice_configuration?.invoice_day_of_week,
      include_bol_images: ratebook.invoice_configuration?.include_bol_images,
      include_delivery_tickets: ratebook.invoice_configuration?.include_delivery_tickets,
      include_invoice_pdf: ratebook.invoice_configuration?.include_invoice_pdf,
      include_invoice_excel: ratebook.invoice_configuration?.include_invoice_excel,
    })
  }, [ratebook, invoiceStatus, form.setFieldsValue])

  // Track form changes
  const [isFormChanged, setIsFormChanged] = useState(false)

  // Handle form changes
  const handleFormChange = () => {
    const hasChanges = form.isFieldsTouched()
    setIsFormChanged(hasChanges)
  }

  // Handle form submission
  const handleSave = async () => {
    try {
      const values = await form.validateFields()
      // Set default day of week to Monday if frequency is Weekly and no day is selected
      const invoice_day_of_week =
        values.billing_frequency === 'Weekly' ? values.invoice_day_of_week || 'Monday' : values.invoice_day_of_week

      const updatedRatebook: Ratebook = {
        ...ratebook,
        freight_method: values.freight_method || 'longest_leg',
        freight_date_method: values.freight_date_method || 'delivery_date',
        invoice_status: ratebook.invoice_status,
        product_minimums: values.product_minimums || [],
        address_info: values.address_info || initializeAddressInfo(),
        invoice_configuration: {
          ...ratebook.invoice_configuration,
          invoice_terms: {
            name: (values?.invoice_terms?.label as 'Net 30' | 'Net 60' | 'Net 45') || 'Net 30',
            days: values?.invoice_terms?.value || 30,
          },
          billing_volume_basis: values.billing_volume_basis || 'Net',
          billing_frequency: values.billing_frequency || 'Monthly',
          invoice_grouping: values.invoice_grouping || 'By Billing Period',
          recipients: values.recipients || [],
          invoice_day_of_week,
          activation_date: ratebook.invoice_configuration?.activation_date || null,
          include_bol_images: values.include_bol_images,
          include_delivery_tickets: values.include_delivery_tickets,
          send_emails: values.send_emails,
          include_invoice_pdf: values.include_invoice_pdf,
          include_invoice_excel: values.include_invoice_excel,
        },
      }

      await mutation.mutateAsync(updatedRatebook)
      queryClient.invalidateQueries({ queryKey: ['ratebook', ratebook._id] })
      setIsFormChanged(false)
      message.success('Invoice configuration updated successfully')
    } catch (error) {
      message.error('Failed to update invoice configuration')
    }
  }

  const handleCancel = () => {
    // Reset form to the current ratebook values
    form.setFieldsValue({
      invoice_terms: {
        label: ratebook.invoice_configuration?.invoice_terms?.name,
        value: ratebook.invoice_configuration?.invoice_terms?.days,
      },
      send_emails: ratebook.invoice_configuration?.send_emails ?? true,
      billing_volume_basis: ratebook.invoice_configuration?.billing_volume_basis,
      billing_frequency: ratebook.invoice_configuration?.billing_frequency,
      invoice_grouping: ratebook.invoice_configuration?.invoice_grouping,
      freight_method: ratebook.freight_method || '',
      freight_date_method: ratebook.freight_date_method || 'delivery_date',
      invoice_status: invoiceStatus,
      recipients: ratebook.invoice_configuration?.recipients || [],
      product_minimums: Array.isArray(ratebook.product_minimums) ? [...ratebook.product_minimums] : [],
      address_info: initializeAddressInfo(ratebook.address_info),
      invoice_day_of_week: ratebook.invoice_configuration?.invoice_day_of_week,
      include_bol_images: ratebook.invoice_configuration?.include_bol_images,
      include_delivery_tickets: ratebook.invoice_configuration?.include_delivery_tickets,
      include_invoice_pdf: ratebook.invoice_configuration?.include_invoice_pdf,
      include_invoice_excel: ratebook.invoice_configuration?.include_invoice_excel,
    })

    setIsFormChanged(false)
    onChange?.('cancelled', true)
    message.info('Changes discarded')
  }

  return {
    form,
    isFormChanged,
    isMetadataLoading,
    metadata,
    handleFormChange,
    handleSave,
    handleCancel,
  }
}
